import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import capitalize from 'lodash.capitalize';
import { formatPrice } from '@chama/utils/functions/currency';
import priceLabels from '@chama/utils/labels/products';
import { getOrderTotalForOrderDetails } from '@chama/utils/functions/order-values';
import Table from '../Table';
import './index.css';

const propTypes = {
  order: PropTypes.object.isRequired,
};
const grey = 'rgba(74, 74, 74, 0.2)';
const tableStyle = { borderColor: grey };
const headerStyle = { backgroundColor: grey };
const subtotalRowStyle = { backgroundColor: 'rgba(74, 74, 74, 0.08)' };

export const ROW_CLASS = 'PRICE_OVERVIEW_ROW';
export const PRODUCT_TOTAL = 'ORDER_PRODUCT_TOTAL';
export const CHANGE_AMOUNT = 'CHANGE_AMOUNT';

class OrderPriceOverview extends Component {

  renderPaymentCell = rowSpan => {
    const {
      order,
      order: { paymentType, isChangeNeeded, changeAmount },
    } = this.props;
    const renderChangeContent = () => {
      if (paymentType === 'cash' && isChangeNeeded && changeAmount) {
        const rest = changeAmount - getOrderTotalForOrderDetails(order, true);

        return (
          <div id={CHANGE_AMOUNT}>
            <FormattedMessage id="change_for" defaultMessage="change for" />
            &nbsp;
            <span>{formatPrice(changeAmount)}</span>
            &nbsp;
            <span>({formatPrice(rest)})</span>
          </div>
        );
      }

      return null;
    };

    return (
      <Table.Cell rowSpan={rowSpan}>
        <div>
          <strong>
            <FormattedMessage
              id={`order_details.payment_method.${paymentType}`}
              defaultMessage={capitalize(paymentType)}
            />
          </strong>
          {renderChangeContent()}
        </div>
      </Table.Cell>
    );
  };

  renderVoucherRow = () => {
    const {
      orderDetails,
    } = this.props;

    return (
      <Table.Row>
        <Table.Cell colSpan="3">
          <div className="OrderPriceOverview__orange">
            <div className="OrderPriceOverview__right_aligned_text">
              <FormattedMessage id='order_detail-product-info.voucherLabel' defaultMessage='Discount voucher' />
            </div>
          </div>
        </Table.Cell>
        <Table.Cell borderRight>
          <div className="OrderPriceOverview__price">
            <span className="OrderPriceOverview__orange">R$ </span>
            <span className="OrderPriceOverview__orange">
              - {formatPrice(orderDetails.voucherDiscount, false)}
            </span>
          </div>
        </Table.Cell>
        <Table.Cell borderRight>

        </Table.Cell>
      </Table.Row>
    );
  };

  renderCreditDiscount = () => {
    const {
      orderDetails: {
        creditDiscount
      },
    } = this.props;

    return (
      <Table.Row>
        <Table.Cell colSpan="3">
          <div className="OrderPriceOverview__orange">
            <div className="OrderPriceOverview__right_aligned_text">
              <FormattedMessage id="order_detail-product-info.creditLabel" defaultMessage="Chama Credits" />
            </div>
          </div>
        </Table.Cell>
        <Table.Cell borderRight>
          <div className="OrderPriceOverview__price">
            <span className="OrderPriceOverview__orange">R$ </span>
            <span className="OrderPriceOverview__orange">
              - {formatPrice(creditDiscount, false)}
            </span>
          </div>
        </Table.Cell>
        <Table.Cell borderRight>

        </Table.Cell>
      </Table.Row>
    );
  };

  renderSubtotalRow = () => {
    const { order, orderDetails } = this.props;

    return (
      <Table.Row style={subtotalRowStyle}>
        <Table.Cell colSpan="3">
          <div className="OrderPriceOverview__right_aligned_text">
            <span>SUBTOTAL</span>
          </div>
        </Table.Cell>
        <Table.Cell borderRight>
          <div className="OrderPriceOverview__price">
            <span>R$ </span>
            <span>{formatPrice(getOrderTotalForOrderDetails(order, orderDetails), false)}</span>
          </div>
        </Table.Cell>
        <Table.Cell borderRight>
        </Table.Cell>
      </Table.Row>
    );
  };

  renderProductRows = () => {
    const {
      order: { products, voucher },
    } = this.props;
    const mapOrderProductsIntoRows = (product, i) => {
      const productBrand =
        product.properties && product.properties.brand ? product.properties.brand : '-';
      const productLabel =
        product.properties && product.properties.type ? priceLabels[product.properties.type] : '-';
      const unitPrice = formatPrice(product.price, false);
      const sumPrice = formatPrice(product.price * product.quantity, false);
      const rowSpan = voucher ? 3 : 1;

      if (i === 0) {
        return (
          <Table.Row className={ROW_CLASS} key={i}>
            <Table.Cell>
              <div>{product.quantity}</div>
            </Table.Cell>
            <Table.Cell>
              <span>{productBrand}</span>
            </Table.Cell>
            <Table.Cell>
              <div>{productLabel}</div>
            </Table.Cell>
            <Table.Cell>
              <div className="OrderPriceOverview__price">
                <span>R$ </span>
                <span>{unitPrice}</span>
              </div>
            </Table.Cell>
            <Table.Cell id={PRODUCT_TOTAL}>
              <div className="OrderPriceOverview__price">
                <span>R$ </span>
                <span>{sumPrice}</span>
              </div>
            </Table.Cell>
            {this.renderPaymentCell((products.length + rowSpan).toString())}
          </Table.Row>
        );
      }

      return (
        <Table.Row className={ROW_CLASS} key={i}>
          <Table.Cell>
            <div>{product.quantity}</div>
          </Table.Cell>
          <Table.Cell>
            <div>{productBrand}</div>
          </Table.Cell>
          <Table.Cell>
            <div>{productLabel}</div>
          </Table.Cell>
          <Table.Cell>
            <div className="OrderPriceOverview__price">
              <span>R$ </span>
              <span>{unitPrice}</span>
            </div>
          </Table.Cell>
          <Table.Cell borderRight>
            <div className="OrderPriceOverview__price">
              <span>R$ </span>
              <span>{sumPrice}</span>
            </div>
          </Table.Cell>
        </Table.Row>
      );
    };

    return products.map(mapOrderProductsIntoRows);
  };

  render() {
    const {
      order,
      order: { voucher },
      orderDetails,
    } = this.props;
    const totalRowStyle = voucher ? headerStyle : {};

    return (
      <Table style={tableStyle}>
        <Table.Header style={headerStyle}>
          <Table.Row>
            <Table.Cell>
              <FormattedMessage id="order_details.table.quantity" defaultMessage="QNTY" />
            </Table.Cell>
            <Table.Cell>
              <FormattedMessage id="order_details.table.brand" defaultMessage="BRAND" />
            </Table.Cell>
            <Table.Cell>
              <FormattedMessage id="order_details.table.product" defaultMessage="PRODUCT" />
            </Table.Cell>
            <Table.Cell>
              <FormattedMessage id="order_details.table.unit_price" defaultMessage="UNIT PRICE" />
            </Table.Cell>
            <Table.Cell>
              <span>TOTAL</span>
            </Table.Cell>
            <Table.Cell>
              <FormattedMessage id="order_details.table.payment" defaultMessage="PAYMENT" />
            </Table.Cell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {this.renderProductRows()}
          {voucher && this.renderSubtotalRow()}
          {orderDetails && orderDetails.voucherDiscount != 0 && this.renderVoucherRow()}
          {orderDetails && orderDetails.creditDiscount != 0 && this.renderCreditDiscount()}
          <Table.Row style={totalRowStyle}>
            <Table.Cell colSpan="4">
              <div className="OrderPriceOverview__right_aligned_text OrderPriceOverview__uppercase_text">
                <strong>
                  <FormattedMessage
                    id="order_details.table.order_total"
                    defaultMessage="ORDER TOTAL"
                  />
                </strong>
              </div>
            </Table.Cell>
            <Table.Cell borderRight>
              <div className="OrderPriceOverview__price">
                <strong>R$ </strong>
                <strong>{formatPrice(getOrderTotalForOrderDetails(order, orderDetails, true), false)}</strong>
              </div>
            </Table.Cell>
          </Table.Row>
        </Table.Body>
      </Table>
    );
  }
}

OrderPriceOverview.propTypes = propTypes;

export default injectIntl(OrderPriceOverview);



// WEBPACK FOOTER //
// ../ui-components/components/OrderPriceOverview/index.jsx